import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import { useWindowSize } from '../hooks/useWindowSize';
import { T, champ as champStyle } from '../theme';
import { API } from '../api';

const COLONNES = [
  { statut: 'En attente',    color: '#f59e0b', bg: '#fffbeb' },
  { statut: 'Gagné',         color: '#059669', bg: '#f0fdf4' },
  { statut: 'Accord envoyé', color: '#3b82f6', bg: '#eff6ff' },
  { statut: 'Gagné facturé', color: '#0891b2', bg: '#ecfeff' },
  { statut: 'Perdu',         color: '#ef4444', bg: '#fff1f2' },
];

const STATUT_COLORS = {
  'En attente': '#f59e0b', 'Validé': '#10b981', "À l'étude": '#3b82f6',
  'Gagné': '#059669', 'Signé': '#7c3aed', 'Perdu': '#ef4444',
  'Sans suite': '#9ca3af', 'Perdu mais à relancer': '#f97316',
  'Accord envoyé': '#3b82f6', 'Gagné facturé': '#0891b2',
};

const inputStyle = champStyle;

const NO_CACHE = { headers: { 'Cache-Control': 'no-store, no-cache', 'Pragma': 'no-cache' } };

function Kanban() {
  const { isMobile, isTablet } = useWindowSize();
  const [prospects, setProspects] = useState([]);
  const [projets, setProjets]     = useState([]);
  const [vendeurs, setVendeurs]   = useState([]);
  const [toast, setToast]         = useState(null);
  const [dragId, setDragId]       = useState(null); // projet id
  const [dragOverStatut, setDragOverStatut] = useState(null);
  const navigate = useNavigate();

  const [filtreVendeur, setFiltreVendeur] = useState('Tous');
  const [filtrePeriode, setFiltrePeriode] = useState('Tous');
  const [recherche, setRecherche]         = useState('');
  const [vue, setVue]                     = useState('kanban');

  const fetchProjets = useCallback(() => {
    axios.get(`${API}/projets`, NO_CACHE).then(res => setProjets(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/prospects`, NO_CACHE).then(res => setProspects(res.data)).catch(() => {});
    axios.get(`${API}/vendeurs`, NO_CACHE).then(res => setVendeurs(res.data)).catch(() => {});
    fetchProjets();
  }, [fetchProjets]);

  useEffect(() => {
    const timer = setInterval(fetchProjets, 30000);
    return () => clearInterval(timer);
  }, [fetchProjets]);

  const getNomSociete = (prospectId) => {
    const p = prospects.find(p => p.id === prospectId);
    return p ? p.nom_societe : '—';
  };

  const getVendeurNom = (vendeurId) => {
    if (!vendeurId) return null;
    return (vendeurs.find(v => v.id === vendeurId) || {}).nom || null;
  };

  const formatK = (val) => {
    if (!val) return null;
    if (val >= 1000000) return (val / 1000000).toFixed(1) + ' M€';
    if (val >= 1000) return Math.round(val / 1000) + ' k€';
    return Math.round(val) + ' €';
  };

  const isAffaireChaude = (dateStr) => {
    if (!dateStr) return false;
    const ech = new Date(dateStr); ech.setHours(23, 59, 59, 0);
    const j = Math.ceil((ech - new Date()) / 86400000);
    return j >= 0 && j <= 120;
  };

  const filtresActifs = filtreVendeur !== 'Tous' || filtrePeriode !== 'Tous' || recherche !== '';

  const cancelDrag = () => { setDragId(null); setDragOverStatut(null); };

  const resetFiltres = () => {
    cancelDrag();
    setFiltreVendeur('Tous');
    setFiltrePeriode('Tous');
    setRecherche('');
  };

  // ── Projets filtrés ────────────────────────────────────────
  const projetsFiltres = useMemo(() => {
    return projets.filter(p => {
      if (recherche) {
        const nom = getNomSociete(p.prospect_id).toLowerCase();
        const mat = (p.nom_produit || p.materiel || '').toLowerCase();
        if (!nom.includes(recherche.toLowerCase()) && !mat.includes(recherche.toLowerCase())) return false;
      }
      if (filtreVendeur !== 'Tous') {
        const vObj = vendeurs.find(v => v.nom === filtreVendeur);
        if (!vObj || p.vendeur_id !== vObj.id) return false;
      }
      if (filtrePeriode !== 'Tous') {
        if (!p.date_echeance) return false;
        const now = new Date(); now.setHours(0, 0, 0, 0);
        const ech = new Date(p.date_echeance); ech.setHours(0, 0, 0, 0);
        if (filtrePeriode === 'retard')    return ech < now;
        if (filtrePeriode === 'semaine')   { const fin = new Date(now); fin.setDate(now.getDate() + 7); return ech >= now && ech <= fin; }
        if (filtrePeriode === 'mois')      return ech.getMonth() === now.getMonth() && ech.getFullYear() === now.getFullYear();
        if (filtrePeriode === 'trimestre') return Math.floor(ech.getMonth() / 3) === Math.floor(now.getMonth() / 3) && ech.getFullYear() === now.getFullYear();
        return false;
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projets, prospects, vendeurs, filtreVendeur, filtrePeriode, recherche]);

  // ── Vue liste triée par échéance ───────────────────────────
  const projetsListe = useMemo(() => {
    return [...projetsFiltres].sort((a, b) => {
      if (!a.date_echeance && !b.date_echeance) return 0;
      if (!a.date_echeance) return 1;
      if (!b.date_echeance) return -1;
      return new Date(a.date_echeance) - new Date(b.date_echeance);
    });
  }, [projetsFiltres]);

  // ── Drag & Drop ────────────────────────────────────────────
  const onDragStart = (e, projetId) => {
    setDragId(projetId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, statut) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStatut(statut);
  };

  const onDrop = (e, statut) => {
    e.preventDefault();
    if (dragId === null) return;
    const projet = projets.find(p => p.id === dragId);
    if (!projet || (projet.statut_projet || 'En attente') === statut) {
      cancelDrag(); return;
    }
    const origStatut = projet.statut_projet;
    setProjets(prev => prev.map(p => p.id === dragId ? { ...p, statut_projet: statut } : p));
    axios.patch(`${API}/projets/${dragId}/statut_projet`, { statut })
      .then(() => {
        setToast({ message: `Statut mis à jour : ${statut}`, type: 'success' });
        fetchProjets();
      })
      .catch(() => {
        setProjets(prev => prev.map(p => p.id === dragId ? { ...p, statut_projet: origStatut } : p));
        setToast({ message: 'Erreur lors de la mise à jour.', type: 'error' });
      });
    cancelDrag();
  };

  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOverStatut(null);
  };

  const BOARD_HEIGHT = 'calc(100vh - 108px)';

  return (
    <div key={`kanban-${filtreVendeur}`} style={{ fontFamily: T.police, display: 'flex', flexDirection: 'column', height: vue === 'kanban' ? BOARD_HEIGHT : 'auto' }}>

      {/* ── En-tête + filtres ─────────────────────────────── */}
      <div style={{ marginBottom: '14px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div>
            <h1 style={T.titre}>Pipeline Kanban</h1>
            <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
              {projetsFiltres.length} projet(s){filtresActifs ? ' — filtrés' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {filtresActifs && (
              <button onClick={resetFiltres} style={{ ...inputStyle, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', cursor: 'pointer', fontWeight: '600' }}>
                ✕ Réinitialiser les filtres
              </button>
            )}
            <button onClick={() => setVue(v => v === 'kanban' ? 'liste' : 'kanban')}
              style={{ ...inputStyle, background: '#1a2b4a', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '500' }}>
              {vue === 'kanban' ? '📋 Vue Liste' : '🗂️ Vue Kanban'}
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="🔍 Rechercher par entreprise ou produit..."
            value={recherche} onChange={e => { cancelDrag(); setRecherche(e.target.value); }}
            style={{ ...inputStyle, width: '260px' }}
          />
          <select value={filtreVendeur} onChange={e => { cancelDrag(); setFiltreVendeur(e.target.value); }} style={{ ...inputStyle, maxWidth: '200px' }}>
            <option value="Tous">Tous vendeurs</option>
            {vendeurs.map(v => <option key={v.id} value={v.nom}>{v.nom}</option>)}
          </select>
          <select value={filtrePeriode} onChange={e => { cancelDrag(); setFiltrePeriode(e.target.value); }} style={inputStyle}>
            <option value="Tous">Toutes périodes</option>
            <option value="semaine">Cette semaine</option>
            <option value="mois">Ce mois</option>
            <option value="trimestre">Ce trimestre</option>
            <option value="retard">En retard</option>
          </select>
        </div>
      </div>

      {/* ── Vue Kanban ────────────────────────────────────── */}
      {vue === 'kanban' && (
        <div
          key={`board|${filtreVendeur}|${filtrePeriode}|${recherche}`}
          style={{ display: 'grid', gridTemplateColumns: isMobile || isTablet ? 'repeat(5, 220px)' : 'repeat(5, 1fr)', gap: '12px', flex: 1, overflowX: isMobile || isTablet ? 'auto' : 'hidden', overflowY: 'hidden' }}>
          {COLONNES.map(col => {
            const cards = projetsFiltres.filter(p => (p.statut_projet || 'En attente') === col.statut);
            const isDragOver = dragOverStatut === col.statut;
            return (
              <div key={col.statut}
                onDragOver={e => onDragOver(e, col.statut)}
                onDrop={e => onDrop(e, col.statut)}
                onDragLeave={onDragLeave}
                style={{
                  background: isDragOver ? col.bg : '#f8fafc',
                  borderRadius: '12px',
                  border: isDragOver ? `2px dashed ${col.color}` : '2px solid transparent',
                  padding: '12px',
                  display: 'flex', flexDirection: 'column',
                  height: '100%', overflow: 'hidden',
                  transition: 'border 0.15s, background 0.15s',
                }}>

                {/* En-tête colonne */}
                <div style={{ marginBottom: '12px', flexShrink: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span translate="no" style={{ fontSize: '12px', fontWeight: '700', color: col.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {col.statut}
                    </span>
                    <span style={{ background: col.color + '20', color: col.color, fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '99px', border: `1px solid ${col.color}40` }}>
                      {cards.length}
                    </span>
                  </div>
                </div>

                {/* Cartes */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, overflowY: 'auto', paddingRight: '2px' }}>
                  {cards.map(p => {
                    const nomSociete = getNomSociete(p.prospect_id);
                    const nomProduit = p.nom_produit || p.materiel || null;
                    const vendeurNom = getVendeurNom(p.vendeur_id);
                    const chaude = isAffaireChaude(p.date_echeance);
                    const isDragging = dragId === p.id;
                    return (
                      <div key={p.id}
                        draggable
                        onDragStart={e => onDragStart(e, p.id)}
                        onDragEnd={cancelDrag}
                        onClick={() => navigate(`/prospects/${p.prospect_id}`)}
                        style={{
                          background: 'white', borderRadius: '10px', padding: '12px',
                          boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.15)' : '0 1px 4px rgba(0,0,0,0.07)',
                          cursor: 'grab', borderLeft: `3px solid ${col.color}`,
                          opacity: isDragging ? 0.5 : 1,
                          transition: 'box-shadow 0.15s, opacity 0.15s',
                          userSelect: 'none',
                        }}>
                        {/* Entreprise */}
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a2b4a', marginBottom: '2px', lineHeight: 1.3 }}>
                          {nomSociete}
                        </div>
                        {/* Produit */}
                        {nomProduit && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', lineHeight: 1.3 }}>
                            {nomProduit}
                          </div>
                        )}
                        {/* Montant */}
                        {p.montant_financement > 0 && (
                          <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '700', marginBottom: '4px' }}>
                            {formatK(p.montant_financement)}
                          </div>
                        )}
                        {/* Montant à facturer */}
                        {col.statut === 'Gagné facturé' && p.montant_a_facturer > 0 && (
                          <div style={{ fontSize: '11px', background: '#cffafe', color: '#155e75', border: '1px solid #67e8f9', borderRadius: '6px', padding: '2px 7px', marginBottom: '4px', fontWeight: '700' }}>{formatK(p.montant_a_facturer)} à facturer
                          </div>
                        )}
                        {/* Vendeur */}
                        {vendeurNom && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>{vendeurNom}</div>
                        )}
                        {/* Organisme */}
                        {p.organisme_financier && (
                          <div style={{ fontSize: '11px', color: '#0891b2', fontWeight: '500', marginTop: '3px' }}>{p.organisme_financier}</div>
                        )}
                        {/* Affaire chaude */}
                        {chaude && (
                          <div style={{ fontSize: '10px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '99px', padding: '1px 6px', marginTop: '5px', fontWeight: '700', display: 'inline-block' }}>Affaire chaude
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {cards.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#d1d5db', fontSize: '12px' }}>
                      {filtresActifs ? 'Aucun résultat' : 'Glissez une carte ici'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Vue Liste ─────────────────────────────────────── */}
      {vue === 'liste' && (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                {['Entreprise', 'Produit / Matériel', 'Statut projet', 'Montant', 'Échéance', 'Vendeur'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projetsListe.length === 0 ? (
                <tr key="empty">
                  <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                    Aucun projet ne correspond aux filtres.
                  </td>
                </tr>
              ) : projetsListe.map(p => {
                const nomSociete = getNomSociete(p.prospect_id);
                const nomProduit = p.nom_produit || p.materiel || '—';
                const vendeurNom = getVendeurNom(p.vendeur_id);
                const enRetard = p.date_echeance && new Date(p.date_echeance) < new Date();
                const statutColor = STATUT_COLORS[p.statut_projet] || '#9ca3af';
                return (
                  <tr key={p.id} onClick={() => navigate(`/prospects/${p.prospect_id}`)}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a2b4a' }}>{nomSociete}</div>
                      {p.organisme_financier && (
                        <div style={{ fontSize: '11px', color: '#0891b2', marginTop: '2px' }}>{p.organisme_financier}</div>
                      )}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: '#374151' }}>{nomProduit}</td>
                    <td style={{ padding: '13px 16px' }}>
                      <span translate="no" style={{ background: statutColor + '20', color: statutColor, fontSize: '12px', fontWeight: '600', padding: '3px 9px', borderRadius: '99px', border: `1px solid ${statutColor}40`, whiteSpace: 'nowrap' }}>
                        {p.statut_projet || 'En attente'}
                      </span>
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', fontWeight: '600', color: p.montant_financement ? '#3b82f6' : '#d1d5db' }}>
                      {formatK(p.montant_financement) || '—'}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', whiteSpace: 'nowrap', color: enRetard ? '#ef4444' : '#374151', fontWeight: enRetard ? '600' : '400' }}>
                      {p.date_echeance ? (enRetard ? '⚠️ ' : '') + new Date(p.date_echeance).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: vendeurNom ? '#374151' : '#d1d5db' }}>
                      {vendeurNom || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default Kanban;
